import React, { useState, useEffect } from "react"
import Button from "@kit/design/button"
import Dialog from "@kit/design/dialog"
import Note from "@kit/design/note"
import useForm, { Form, useFormContext } from "@kit/design/use-form"
import Label from "@kit/design/label"
import Input from "@kit/design/input"
import CheckboxField from "@kit/design/checkbox-field"
import Stack from "@kit/design/stack"
import FormData, { useFormInfo } from "app/forms/data/form-data"
import EditNameInline from "../../app/shared/components/edit-name-inline"
import Builder from "../../app/forms/builder/builder"
import useFetch from "../../hooks/use-new-fetch"
import { withoutNull } from "../../app/forms/data/filters"
import { capitalize } from "lodash"
import cx from "classnames"
import VisuallyHidden from "@kit/design/visually-hidden"
import useToast from "@kit/design/use-toast"
import padlock from "./assets/padlock.svg?inline"
import QuoteMark from "./assets/quote-mark.svg"
import useTimeout from "hooks/use-timeout"
import loadLinkmink from "../../utils/load-linkmink"

const DEFAULT_SIDEBAR_SECTION = "General styles"

const HiddenLabelTextField = ({ id, name, label, placeholder, type }) => {
  const form = useFormContext()
  const value = form.getValue(name)
  const handleBlur = () => {
    form.setTouched(name)
  }
  const handleChange = event => {
    const { value } = event.target
    form.setValue(name, value)
  }
  return (
    <div className="w-11/12">
      <VisuallyHidden>
        <Label htmlFor={id}>{label}</Label>
      </VisuallyHidden>
      <Input
        id={id}
        name={name}
        value={value || ""}
        placeholder={placeholder}
        type={type}
        className="font-semibold"
        style={{
          boxShadow: "0px 4px 24px rgba(105, 113, 119, 0.12)",
          borderColor: "#ffffff",
          borderRadius: "4px",
          padding: "1rem"
        }}
        onBlur={handleBlur}
        onChange={handleChange}
      />
    </div>
  )
}

function Errors({ errors }) {
  const errorItems = Object.keys(errors).map((key, index) => {
    const label = capitalize(key)
    const message = errors[key].join("; ")
    const className = cx({ "mt-1": index > 0 })

    return <li className={className} key={key}>{`${label} ${message}`}</li>
  })

  return (
    <Note className="mb-2" type="warning">
      <ul className="list-none p-0">{errorItems}</ul>
    </Note>
  )
}

function SignupModal({ onClose, name }) {
  const form = useForm({ initialData: { user: { terms_of_service: false } } })

  const [request, loading] = useFetch("/")
  const [errors, setErrors] = useState(null)
  const { form: landingPage } = useFormInfo()
  const [linkminkId, setLinkminkId] = useState(null)

  useEffect(() => {
    loadLinkmink()
      .then(() => {
        /* eslint-disable-next-line no-undef */
        setLinkminkId(lmFinished())
      })
      .catch(() => {
        console.log("Error fetching Linkmink ID")
      })
  }, [])

  const handleSubmit = async event => {
    event.preventDefault()
    event.stopPropagation()
    setErrors(null)

    const page = { ...landingPage.pages[0], name }

    try {
      const { data: responseData } = await request.post({
        path: "users",
        data: {
          user: {
            ...form.data.user
          },
          registration: {
            stripe_plan: "free-lp",
            linkmink_ref: linkminkId
          },
          landing_page: {
            ...page,
            elements: page.elements.filter(withoutNull),
            template: { name: page.template }
          }
        }
      })

      window.location = `/forms/designers/${responseData.id}/edit`
    } catch (error) {
      if (!error.response) {
        console.error(error)
        return
      }

      const json = await error.response.json()
      setErrors(json.errors)
    }
  }

  return (
    <Dialog label="Signup" onClose={onClose}>
      <div className="p-16 rounded">
        <h1 className="text-2xl font-bold font-serif text-gray-800">
          Your page is looking good! Let’s get it saved.
        </h1>
        <p className="pt-4 text-base text-gray-700">
          Enter your email address to save your page. Once it’s saved you’ll
          unlock email sending, custom domains, and so many more features!
        </p>
        <Form form={form} onSubmit={handleSubmit} className="mt-16">
          <Stack spacing={5}>
            {errors && <Errors errors={errors} />}
            <HiddenLabelTextField
              id="user-email"
              name="user[email]"
              label="Your email address"
              type="text"
              placeholder="Your email address"
            />
            <HiddenLabelTextField
              id="user-password"
              name="user[password]"
              className="w-full"
              type="password"
              placeholder="Your password"
            />

            <CheckboxField
              name="user[terms_of_service]"
              label={
                <span className="text-gray-700 inline-block pt-1">
                  By joining, you agree to the{" "}
                  <a
                    href="https://kit.com/terms/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    terms of service
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://kit.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    privacy policy
                  </a>
                  .
                </span>
              }
            />
          </Stack>

          <button className="hidden" type="submit">
            Submit
          </button>

          <Button
            color="green"
            disabled={loading}
            onClick={handleSubmit}
            className="mt-5"
            style={{ marginLeft: "0" }}
          >
            Create your account
          </Button>
        </Form>
        <div className="mt-16">
          <QuoteMark className="w-4 mx-auto" />
        </div>
        <div className="w-5/6 mx-auto mt-8">
          <p className="mb-8 text-center font-serif text-gray-800 leading-relaxed">
            The ease of design all on one page with edit tools and visuals
            resources is terrific! Kit speaks my language, and is designed for
            creatives, allowing me to create Forms and Landing Pages with ease.
          </p>
          <div className="flex items-center justify-center">
            <img
              src="https://i.ibb.co/0fMPYJS/Picture15.jpg"
              className="rounded-full mr-8"
              width="70"
              height="70"
              alt="Dorene Olsen"
            />
            <div>
              <span className="font-semibold text-base text-gray-800">
                Dorene Olsen
              </span>
              <div className="text-sm text-gray-700">savoryinsights.com</div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

function PagesCreate({ template }) {
  const [name, setName] = useState("My Landing Page")
  const [signupOpen, setSignupOpen] = useState(false)
  const [activeSection, setActiveSection] = useState(DEFAULT_SIDEBAR_SECTION)
  const [activeElement, setActiveElement] = useState(null)
  const toastTimer = 5 * 60 * 1000
  const toast = useToast()

  useTimeout(() => {
    if (!signupOpen) {
      showToast()
    }
  }, toastTimer)

  const showToast = () => {
    toast({
      type: "cta",
      text:
        "Save your progress and get access to more features including custom domains, additional field types, thank you pages, and more.",
      icon: padlock,
      actions: [{ text: "Save", onClick: () => setSignupOpen(true) }],
      duration: null
    })
  }

  return (
    <div className="w-full">
      <FormData
        initialSettings={[]}
        initialPages={[
          {
            id: 0,
            name,
            template: template.name,
            root_page: true,
            display_type: "page",
            elements: [],
            styles: []
          }
        ]}
        editing={true}
        action={""}
        id={0}
        uid={""}
        template={template}
        format={null}
        ckjsVersion={""}
        metadata={{}}
        url={""}
        htmlUrl={""}
      >
        <div className="bg-white flex border-b border-gray-200">
          <div className="flex-grow">
            <EditNameInline
              className="ml-4"
              name={name}
              onChange={name => {
                setName(name)
              }}
            />
          </div>
          <div className="p-3">
            <Button is="primary" onClick={() => setSignupOpen(true)}>
              Save
            </Button>
          </div>
        </div>
        <Builder
          activeSection={activeSection || DEFAULT_SIDEBAR_SECTION}
          setActiveSection={setActiveSection}
          activeElement={activeElement}
          setActiveElement={setActiveElement}
        />
        {signupOpen && (
          <SignupModal name={name} onClose={() => setSignupOpen(false)} />
        )}
      </FormData>
    </div>
  )
}

export default PagesCreate
